import { Plus } from "lucide-react";
import React, { useEffect } from "react";

import { IgPaper, IgTypography } from "@intelligems/ig-theme";
import { CircularProgress, Stack, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { fromGqlId } from "@src/api/formatters/shopify";
import { AddAnotherButton } from "@src/components/Utils/AddAnotherButton";
import { CustomEventProductPickerWrapper } from "@src/features/customEvents/components/subpages/CustomEventProductPickerWrapper";
import { ProductListItem } from "@src/features/customEvents/components/subpages/ProductListItem";
import { useDispatch, useSelector } from "@src/store";
import {
  getCustomerEventSelectedProducts,
  getCustomerEventSelectedProductsLoading,
  removeCustomEventProduct,
} from "@src/store/slices/customEvents";
import { loadCustomEventSelectedProducts } from "@src/store/slices/customEvents/thunks/loadCustomEventSelectedProducts";
import { isProductPickerOpen } from "@src/store/slices/productPicker/selectors/isProductPickerOpen";
import { openCustomEventsProductPicker } from "@src/store/slices/productPicker/thunks/customEvents/openCustomEventsProductPicker";

interface ProductPageViewProps {
  isArchived: boolean;
}

export const ProductPageView = ({ isArchived }: ProductPageViewProps) => {
  const dispatch = useDispatch();
  const productPickerOpen = useSelector(isProductPickerOpen("customEvents"));
  const selectedProducts = useSelector(getCustomerEventSelectedProducts);
  const selectedProductsLoading = useSelector(
    getCustomerEventSelectedProductsLoading
  );
  const theme = useTheme();
  useEffect(() => {
    void dispatch(loadCustomEventSelectedProducts());
  }, []);

  if (selectedProductsLoading) {
    return (
      <Stack
        mt={5}
        spacing={1}
        direction={"column"}
        justifyContent={"center"}
        textAlign={"center"}
      >
        <IgTypography>Loading Products...</IgTypography>

        <CircularProgress size={25} />
      </Stack>
    );
  }
  return (
    <Stack direction={"column"} marginTop={"15px"} sx={{ width: "100%" }}>
      {!productPickerOpen && selectedProducts.length === 0 && (
        <IgPaper
          sx={{
            boxShadow: "none",
            borderRadius: "5px",
            "&:hover": {
              cursor: "pointer",
              border: `1px solid ${theme.igBorders.accent.default}`,
              backgroundColor: theme.igPrimitives.gray[200],
            },
            width: "100%",
          }}
          onClick={() => void dispatch(openCustomEventsProductPicker())}
        >
          <Stack
            direction={"column"}
            alignItems={"center"}
            paddingLeft={"40px"}
            paddingRight={"40px"}
            paddingTop={"10px"}
            paddingBottom={"10px"}
            width={"100%"}
          >
            <Plus size={36} />

            <IgTypography>Add Products</IgTypography>
          </Stack>
        </IgPaper>
      )}
      {/* Selected Products List */}
      <Stack direction={"column"} marginTop={"10px"}>
        {selectedProducts.map((product) => (
          <ProductListItem
            key={product.id}
            product={product}
            isArchived={isArchived}
            onRemove={() => {
              dispatch(
                removeCustomEventProduct({ productId: fromGqlId(product.id) })
              );
            }}
          />
        ))}
      </Stack>
      {selectedProducts.length > 0 && !isArchived && (
        <AddAnotherButton
          onAddClick={() => void dispatch(openCustomEventsProductPicker())}
          itemLabel={"Add another product"}
        />
      )}
      {productPickerOpen && (
        <Grid>
          <CustomEventProductPickerWrapper />
        </Grid>
      )}
    </Stack>
  );
};
